import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Heart, UserCircle, Scale, ChevronDown, LogOut } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CompareModal from '../components/CompareModal';
import { useCountry } from '../context/CountryContext';

const ERealtyLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { activeCountry } = useCountry();
    const { countryCode = activeCountry?.code?.toLowerCase() || 'eg' } = useParams<{ countryCode: string }>();
    const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [favorites, setFavorites] = useState<any[]>([]); // Array of IDs
    const [compareList, setCompareList] = useState<any[]>([]); // Array of Objects

    const toggleFavorite = (id: any) => {
        setFavorites(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };

    const toggleCompare = (unit: any) => {
        setCompareList(prev => {
            const exists = prev.find(u => u.id === unit.id);
            if (exists) {
                return prev.filter(u => u.id !== unit.id);
            }
            return [...prev, unit];
        });
    };

    const clearCompareList = () => {
        setCompareList([]);
    };

    const getPath = (path: string) => {
        const prefix = `/${countryCode}/erealty`;
        return `${prefix}/${path}`;
    };
    
    // Determine active tab based on path
    const getActiveTab = () => {
        if (location.pathname.includes('/units')) return 'units';
        if (location.pathname.includes('/launches')) return 'launches';
        if (location.pathname.includes('/offers')) return 'offers';
        return 'projects';
    };

    const activeTab = getActiveTab();

    const tabs = [
        { id: 'projects', label: 'Projects', path: getPath('dashboard') },
        { id: 'units', label: 'Units', path: getPath('units') },
        { id: 'launches', label: 'Launches', path: getPath('launches') },
        { id: 'offers', label: 'Offers', path: getPath('offers') },
    ];

    return (
        <div className="h-screen bg-[#F8F9FA] flex flex-col font-sans overflow-hidden">
            {/* Top Navbar — Professional Institutional Design */}
            <header className="bg-white border-b border-gray-100 shrink-0 z-50">
                <div className="max-w-[1720px] mx-auto px-4 h-[64px] flex items-center justify-between">
                    {/* Logo & Category Bridge */}
                    <div className="flex items-center gap-12">
                        <div 
                            className="flex items-center cursor-pointer transition-opacity hover:opacity-80 active:opacity-100 ml-1" 
                            onClick={() => navigate(`/${countryCode}/erealty/dashboard`)}
                        >
                            <img src="/laqysymbolandlogo_premium.svg" alt="LAQY" className="h-7 w-auto" />
                        </div>

                        {/* Navigation Tabs — Minimalist Labeling */}
                        <nav className="hidden md:flex items-center h-full relative">
                            {tabs.map(tab => {
                                const isActive = activeTab === tab.id;
                                return (
                                    <NavLink
                                        key={tab.id}
                                        to={tab.path}
                                        className={`
                                            px-4 h-[64px] flex items-center text-[13px] font-semibold transition-all relative
                                            ${isActive ? 'text-[#1E4164]' : 'text-[#64748B] hover:text-[#1E4164]'}
                                        `}
                                    >
                                        <span className="relative z-10">{tab.label}</span>
                                        {isActive && (
                                            <motion.div 
                                                layoutId="activeTabUnderline"
                                                className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#F0511B]"
                                            />
                                        )}
                                    </NavLink>
                                );
                            })}
                        </nav>
                    </div>

                    {/* Meta Actions Hub */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                            {compareList.length > 0 && (
                                <button 
                                    onClick={() => setIsCompareModalOpen(true)}
                                    className="w-8 h-8 flex items-center justify-center text-[#94A3B8] hover:text-[#1E4164] transition-colors relative"
                                    title="Compare Properties"
                                >
                                    <Scale size={20} strokeWidth={1.5} />
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#1E4164] text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-white">
                                        {compareList.length}
                                    </span>
                                </button>
                            )}
                            <button 
                                onClick={() => navigate(`/${countryCode}/erealty/favorites`)}
                                className="w-8 h-8 flex items-center justify-center text-[#94A3B8] hover:text-[#1E4164] transition-colors relative"
                                title="Favorites"
                            >
                                <Heart size={20} strokeWidth={1.5} />
                                {favorites.length > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#F0511B] text-white text-[9px] font-bold rounded-full flex items-center justify-center border border-white">
                                        {favorites.length}
                                    </span>
                                )}
                            </button>
                        </div>

                        <div className="relative">
                            <button 
                                onClick={() => setIsProfileOpen(!isProfileOpen)}
                                className="flex items-center gap-1 transition-colors group"
                            >
                                <UserCircle size={24} strokeWidth={1.5} className="text-[#1E4164] transition-colors group-hover:text-[#F0511B]" />
                                <ChevronDown size={11} className={`text-[#94A3B8] transition-transform duration-200 ${isProfileOpen ? 'rotate-180' : ''}`} strokeWidth={3} />
                            </button>

                            <AnimatePresence>
                                {isProfileOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 4 }}
                                        className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden"
                                    >
                                        <div className="p-4 border-b border-gray-50 bg-gray-50/50">
                                            <p className="text-[13px] font-bold text-[#1E4164] truncate">Youssef</p>
                                            <p className="text-[11px] text-gray-500 truncate mt-0.5">ceo@laqy.net</p>
                                        </div>
                                        <div className="p-1">
                                            <button className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-[#4A5568] hover:bg-gray-50 rounded-md transition-colors font-medium">
                                                <UserCircle size={16} /> Edit Profile
                                            </button>
                                            <button 
                                                onClick={() => {
                                                    setIsProfileOpen(false);
                                                }}
                                                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-[#F0511B] hover:bg-red-50 rounded-md transition-colors font-medium group"
                                            >
                                                <LogOut size={16} /> Logout
                                            </button>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </header>

            <main className="flex-1 relative">
                <Outlet context={{ favorites, compareList, toggleFavorite, toggleCompare }} />
            </main>

            {/* Modals */}
            <CompareModal 
                isOpen={isCompareModalOpen} 
                onClose={() => setIsCompareModalOpen(false)} 
                compareList={compareList}
                onRemove={toggleCompare}
                onClearAll={clearCompareList}
            />
        </div>
    );
};

export default ERealtyLayout;
